import React, { useEffect, useState } from 'react';
import QRCode from 'qrcode'; // npm install qrcode

// Confirmed against a live Authentik instance on 2026-07-12:
//   challenge.raw.config_url -> otpauth://totp/... URI
//   submit: { code: "123456" }

const lbl = {
  display: 'block',
  fontSize: '13px',
  fontWeight: '500',
  color: '#5F5E5A',
  marginBottom: '5px',
};

const inp = {
  width: '100%',
  padding: '10px 14px',
  border: '1.5px solid #D3D1C7',
  borderRadius: '8px',
  fontSize: '20px',
  textAlign: 'center',
  letterSpacing: '4px',
  boxSizing: 'border-box',
  outline: 'none',
};

const btn = {
  width: '100%',
  padding: '14px',
  background: 'linear-gradient(135deg, #009FE3 0%, #0072B1 100%)',
  color: '#fff',
  border: 'none',
  borderRadius: '10px',
  fontSize: '16px',
  fontWeight: '600',
  cursor: 'pointer',
  marginTop: '8px',
};

const btnDisabled = { ...btn, opacity: 0.6, cursor: 'not-allowed' };

const copyBtn = {
  flexShrink: 0,
  padding: '6px 12px',
  background: '#fff',
  border: '1px solid #D3D1C7',
  color: '#0072B1',
  borderRadius: '6px',
  fontSize: '12px',
  fontWeight: '600',
  cursor: 'pointer',
};

const storeBadge = {
  display: 'inline-block',
  padding: '8px 16px',
  background: '#1a1a1a',
  color: '#fff',
  borderRadius: '8px',
  fontSize: '12px',
  fontWeight: '600',
  textDecoration: 'none',
};

const goToAuthBtn = {
  display: 'inline-block',
  padding: '12px 20px',
  background: '#fff',
  border: '2px solid #0072B1',
  color: '#0072B1',
  borderRadius: '10px',
  fontSize: '14px',
  fontWeight: '700',
  textDecoration: 'none',
};

// Any authenticator app that reads a QR code understands this exact same
// otpauth:// URI (scanning a QR just opens this URI) — Google
// Authenticator's own store listing is used as a safe, free, always-
// available default recommendation, not because this app is tied to it.
const ANDROID_STORE_URL = 'https://play.google.com/store/apps/details?id=com.google.android.apps.authenticator2';
const IOS_STORE_URL = 'https://apps.apple.com/app/google-authenticator/id388497605';

const isMobileDevice = typeof navigator !== 'undefined' && /Android|iPhone|iPad|iPod/i.test(navigator.userAgent || '');

export default function TotpSetupStep({ configUrl, loading, onSubmit, description, dark, pendingUser, avatar, mandatory }) {
  const [qrDataUrl, setQrDataUrl] = useState(null);
  const [code, setCode] = useState('');
  const [copied, setCopied] = useState(false);

  const labelStyle = dark ? { ...lbl, color: 'rgba(255, 255, 255, 0.85)' } : lbl;
  const bodyColor = dark ? 'rgba(255, 255, 255, 0.75)' : '#666';
  const nameColor = dark ? '#fff' : '#1a1a1a';

  const manualSecret = extractSecret(configUrl);

  async function copySecret() {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(manualSecret);
      } else {
        // Fallback for non-secure contexts / older browsers without the
        // Clipboard API — select+copy via a throwaway offscreen textarea.
        const textarea = document.createElement('textarea');
        textarea.value = manualSecret;
        textarea.style.position = 'fixed';
        textarea.style.opacity = '0';
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // Clipboard access denied/unavailable — the code is still visible
      // and selectable manually, so there's nothing further to do here.
    }
  }

  useEffect(() => {
    if (!configUrl) return;
    QRCode.toDataURL(configUrl, { width: 200, margin: 1 })
      .then(setQrDataUrl)
      .catch(() => setQrDataUrl(null));
  }, [configUrl]);

  return (
    <div>
      {pendingUser && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', marginBottom: '16px' }}>
          {avatar && <img src={avatar} alt="" width={36} height={36} style={{ borderRadius: '50%' }} />}
          <span style={{ fontSize: '14px', fontWeight: '600', color: nameColor }}>{pendingUser}</span>
        </div>
      )}
      {mandatory && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: '10px',
          background: dark ? 'rgba(255,255,255,0.1)' : '#FFF6DA',
          border: dark ? '1px solid rgba(255,255,255,0.25)' : '1px solid #F0DFA0',
          borderRadius: '8px', padding: '10px 14px', marginBottom: '16px',
        }}>
          <span style={{ fontSize: '18px' }}>🔒</span>
          <p style={{ margin: 0, fontSize: '13px', fontWeight: '600', color: dark ? '#fff' : '#8A6D00' }}>
            An authenticator app is mandatory — your account can't be created until it's set up.
          </p>
        </div>
      )}

      <p style={{ textAlign: 'center', color: bodyColor, fontSize: '14px', marginBottom: '16px' }}>
        {description || 'This step is required to finish creating your account.'}
      </p>

      {mandatory && (
        <div style={{ textAlign: 'center', marginBottom: '20px' }}>
          <p style={{ fontSize: '13px', fontWeight: '700', color: nameColor, marginBottom: '10px' }}>
            Don't have an authenticator app yet? Install one first:
          </p>
          <div style={{ display: 'flex', gap: '10px', justifyContent: 'center', marginBottom: '8px' }}>
            <a href={ANDROID_STORE_URL} target="_blank" rel="noreferrer" style={storeBadge}>
              ▶ Get it on Google Play
            </a>
            <a href={IOS_STORE_URL} target="_blank" rel="noreferrer" style={storeBadge}>
               Download on the App Store
            </a>
          </div>
          <p style={{ fontSize: '11px', color: bodyColor }}>
            Any TOTP authenticator app works — Google Authenticator, Microsoft Authenticator, Authy, etc.
          </p>
        </div>
      )}

      {qrDataUrl && (
        <div style={{ textAlign: 'center', marginBottom: '16px' }}>
          <img src={qrDataUrl} alt="Scan with your authenticator app" width={200} height={200} />
        </div>
      )}

      {mandatory && isMobileDevice && configUrl && (
        <div style={{ textAlign: 'center', marginBottom: '20px' }}>
          <a href={configUrl} style={goToAuthBtn}>Go to Authenticator App →</a>
          <p style={{ fontSize: '11px', color: bodyColor, marginTop: '8px' }}>
            Opens your installed authenticator app with this account already filled in — you'll still
            need to come back here and type the 6-digit code it shows you.
          </p>
          <p style={{ fontSize: '11px', color: bodyColor, marginTop: '4px' }}>
            Nothing happens when you tap it? That can happen if you opened this page from inside your
            SMS or email app. Just copy the code below into your authenticator app manually instead.
          </p>
        </div>
      )}

      <p style={{ fontSize: '13px', color: bodyColor, marginBottom: '8px' }}>
        Open your authenticator app and scan the code above, or choose{' '}
        <strong>“enter code manually”</strong> and type this instead:
      </p>
      {manualSecret && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: '8px',
          padding: '10px', background: '#f5f5f5',
          borderRadius: '8px', marginBottom: '20px',
        }}>
          <code style={{ fontSize: '13px', wordBreak: 'break-all', flex: 1 }}>
            {manualSecret}
          </code>
          <button type="button" onClick={copySecret} style={copyBtn}>
            {copied ? 'Copied!' : 'Copy'}
          </button>
        </div>
      )}

      <form onSubmit={(e) => { e.preventDefault(); onSubmit({ code }); }}>
        <label style={labelStyle}>Enter the 6-digit code from your app *</label>
        <input
          style={inp}
          type="text"
          inputMode="numeric"
          pattern="[0-9]{6}"
          maxLength={6}
          value={code}
          onChange={(e) => setCode(e.target.value)}
          autoFocus
          required
        />
        <button type="submit" disabled={loading || code.length !== 6} style={(loading || code.length !== 6) ? btnDisabled : btn}>
          {loading ? 'Verifying…' : 'Confirm and continue'}
        </button>
      </form>
    </div>
  );
}

function extractSecret(configUrl) {
  if (!configUrl) return null;
  try {
    const url = new URL(configUrl.replace('otpauth://', 'https://'));
    return url.searchParams.get('secret');
  } catch {
    return null;
  }
}
